import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePage } from "@inertiajs/react";
import axios from "axios";
import LoadingSpinner from "@sprout/loading_spinner.tsx";
import { Dialog, DialogContent, DialogTitle } from "@components/ui/dialog.tsx";
import { useShowFlash } from "@sprout/flash.tsx";
import useTranslation from "@hooks/use_translation.ts";
import {
  SubscriptionForm,
  type SubscriptionFormData,
} from "@components/job_alerts/subscription_form.tsx";

export type SubscriptionModalContextValue = {
  openModal: (path: string) => void;
};

export const SubscriptionModalContext = createContext<SubscriptionModalContextValue | null>(null);

type PageProps = {
  subscriptionForm?: SubscriptionFormData;
  [key: string]: unknown;
};

export const SubscriptionModalProvider = ({ children }: { children: ReactNode }) => {
  const { subscriptionForm } = usePage<PageProps>().props;
  const { t } = useTranslation("misc");
  const showFlash = useShowFlash();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<SubscriptionFormData | null>(null);
  const requestId = useRef(0);
  const consumedPath = useRef<string | null>(null);

  const onError = useCallback(() => showFlash(t("defaultError"), "error"), [showFlash, t]);

  useEffect(() => {
    if (!subscriptionForm || consumedPath.current === subscriptionForm.subscriptionPath) return;

    consumedPath.current = subscriptionForm.subscriptionPath;
    requestId.current += 1;
    setData(subscriptionForm);
    setOpen(true);
  }, [subscriptionForm]);

  const openModal = useCallback(
    (path: string) => {
      const id = requestId.current + 1;
      requestId.current = id;

      setData(null);
      setOpen(true);

      axios
        .get<SubscriptionFormData>(path, { headers: { Accept: "application/json" } })
        .then((response) => {
          if (requestId.current !== id) return;

          if (!response.data?.subscriptionPath) {
            setOpen(false);
            onError();
            return;
          }

          setData(response.data);
        })
        .catch(() => {
          if (requestId.current !== id) return;
          setOpen(false);
          onError();
        });
    },
    [onError],
  );

  const close = useCallback(() => {
    requestId.current += 1;
    setOpen(false);
  }, []);

  const onOpenChange = useCallback(
    (next: boolean) => {
      if (!next) close();
    },
    [close],
  );

  const contextValue = useMemo(() => ({ openModal }), [openModal]);

  return (
    <SubscriptionModalContext.Provider value={contextValue}>
      {children}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          hideClose={false}
          aria-describedby={undefined}
          className="subscription--modal flex flex-col gap-6 sm:max-w-[576px] max-h-[90vh] overflow-y-auto"
          data-provides="subscription-modal"
        >
          {data ? (
            <SubscriptionForm key={data.subscriptionPath} {...data} onClose={close} />
          ) : (
            <>
              <DialogTitle className="sr-only">
                <span>{t("loading")}</span>
              </DialogTitle>
              <div className="flex justify-center py-10">
                <LoadingSpinner size="medium" />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </SubscriptionModalContext.Provider>
  );
};
